import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PrebuiltSettings, selectedMembership, useAxios } from '@intly/core';
import DialogTitle from '../../Dialog/DialogTitle';
import Banner from '../../../ui/Banner/Banner';
import {
  COST_CENTER_REVIEW,
  CostCenterReviewFteRows,
} from './costCenterReview.types';
import { tableSx } from './reviewTableStyles';

interface ReviewFteDialogProps {
  // The period whose FTE figure was clicked; null when closed
  period: string | null;
  onClose: () => void;
  companyId: string;
  companyName: string;
  costCenterColumn: string | null;
  costCenter: string | null;
  costCenterLabel: string;
  settings: PrebuiltSettings;
}

const fteFormat = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const countFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 2,
});

// "Drilldown – FTE": the employees behind one period's FTE figure, adding up to it.
const ReviewFteDialog: FC<ReviewFteDialogProps> = ({
  period,
  onClose,
  companyId,
  companyName,
  costCenterColumn,
  costCenter,
  costCenterLabel,
  settings,
}) => {
  const { t } = useTranslation();
  const { backendDataRequest } = useAxios();
  const groupId = useReactiveVar(selectedMembership);

  const query = useQuery<CostCenterReviewFteRows>({
    queryKey: [
      'prebuilt',
      COST_CENTER_REVIEW,
      'fte',
      groupId,
      companyId,
      costCenter,
      period,
      settings,
    ],
    enabled: Boolean(period && groupId),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await backendDataRequest('data', {
        method: 'POST',
        data: {
          function: 'prebuilt_dashboard_fte_rows',
          groupId,
          dashboard: COST_CENTER_REVIEW,
          companyId,
          period,
          costCenterColumn,
          costCenter,
          settings,
        },
      });
      if (!response?.data || response.data.error) {
        throw new Error(response?.data?.error ?? 'No result');
      }
      return response.data as CostCenterReviewFteRows;
    },
  });

  const data = query.data;
  const totalFte = data?.rows.reduce((sum, row) => sum + row.fte, 0) ?? 0;
  const totalHeadcount =
    data?.rows.reduce((sum, row) => sum + row.headcount, 0) ?? 0;

  return (
    <Dialog fullWidth maxWidth="md" open={Boolean(period)} onClose={onClose}>
      <DialogTitle onClose={onClose}>
        {`${t('FTE_DRILLDOWN')} ${period ?? ''}`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${t('COMPANY')}: ${companyName} · ${t(
            'COST_CENTER'
          )}: ${costCenterLabel}`}
          {data &&
            ` · ${t('REVIEW_TRANSACTIONS_ROWS', { count: data.rows.length })}`}
        </Typography>

        {query.isError && (
          <Banner
            severity="error"
            message={`${t('FTE_DRILLDOWN_LOAD_ERROR')}: ${
              (query.error as Error).message
            }`}
          />
        )}
        {data?.truncated && (
          <Banner
            severity="warning"
            message={t('FTE_DRILLDOWN_TRUNCATED') as string}
          />
        )}
        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {data && !data.rows.length && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {t('FTE_DRILLDOWN_EMPTY')}
          </Typography>
        )}
        {data && data.rows.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 'calc(100vh - 260px)' }}
          >
            <Table stickyHeader size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('FTE_DRILLDOWN_EMPLOYEE')}</TableCell>
                  <TableCell align="right">
                    {t('FTE_DRILLDOWN_EMPLOYEE_NUMBER')}
                  </TableCell>
                  <TableCell align="right">
                    {t('FTE_DRILLDOWN_PERIOD')}
                  </TableCell>
                  <TableCell align="right">
                    {t('FTE_DRILLDOWN_EMPLOYMENT_RATE')}
                  </TableCell>
                  <TableCell align="right">{t('FTE')}</TableCell>
                  <TableCell align="right">
                    {t('FTE_DRILLDOWN_HEADCOUNT')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row, i) => (
                  <TableRow key={`${row.employeeNumber ?? ''}-${i}`} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">
                      {row.employeeNumber ?? ''}
                    </TableCell>
                    <TableCell align="right">{row.period}</TableCell>
                    <TableCell align="right">
                      {row.employmentRate === null ||
                      row.employmentRate === undefined
                        ? ''
                        : `${countFormat.format(row.employmentRate)} %`}
                    </TableCell>
                    <TableCell align="right">
                      {fteFormat.format(row.fte)}
                    </TableCell>
                    <TableCell align="right">
                      {countFormat.format(row.headcount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                  '& .MuiTableCell-root': {
                    fontWeight: 'bold',
                    color: 'text.primary',
                  },
                }}
              >
                <TableRow>
                  <TableCell>{t('FTE_DRILLDOWN_TOTAL')}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">
                    {fteFormat.format(totalFte)}
                  </TableCell>
                  <TableCell align="right">
                    {countFormat.format(totalHeadcount)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewFteDialog;
